import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { get, map, sortBy } from "lodash";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import CreatableSelect from "react-select/creatable";
import styled from "styled-components";
import { FontWeight, Spacing, useTheme } from "@glossy";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { useKeyboardShortcuts, useOnClickOutside } from "@lkf/hooks";
import { boldifyString } from "@tootools/string";
import Chip from "@ui-kit/form/chip";
import Loader from "@ui-kit/loading";
import SafeHTML from "@ui-kit/safe-html";
import Tooltip from "@ui-kit/tooltip";
import { Choice, Status } from "./model";
import getCustomStyles from "./styles";
import { checkFilledCategories, customText } from "./utils";

interface IProps {
  choices: Choice[];
  onSelect: (choice: Choice, status: Status) => void;
  onQueryChange?: (value: string) => void;
  placeholder?: string;
  allowExclusion?: boolean;
  freeText?: boolean;
  locale?: Locale;
  noOptionMessage?: string;
  firstMount?: boolean;
  autoFocus?: boolean;
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  tooltip?: (choice: string) => string;
  onClickSelectedOption?: (choice: Choice) => void;
  query?: string;
}

const Actions = styled.div`
  display: flex;
  > * + * {
    margin-left: 12px;
  }
`;

const useIncludeExclude = (setMenuOpened, onSelect, { data, value }: any) => {
  const handleInclusion = useCallback(
    (e) => {
      e.stopPropagation();
      setMenuOpened(false);
      onSelect(data.__isNew__ ? customText(value, data) : data, "include");
    },
    [setMenuOpened, onSelect, data, value]
  );

  const handleExclusion = useCallback(
    (e) => {
      e.stopPropagation();
      setMenuOpened(false);
      onSelect(data, "exclude");
    },
    [setMenuOpened, onSelect, data]
  );

  return { handleInclusion, handleExclusion };
};

const Highlight = styled.div`
  em,
  b {
    font-weight: ${FontWeight.Bold};
  }
`;

const CustomOption = ({
  children,
  getStyles,
  onSelect,
  onQueryChange,
  locale,
  status,
  allowExclusion,
  freeText,
  innerRef,
  innerProps,
  setMenuOpened,
  query,
  ...props
}: {
  onQueryChange?: (value: string) => void;
  locale?: Locale;
} & Record<string, any>) => {
  useKeyboardShortcuts("autocomplete_option", {
    enter: () => {
      if (props.isFocused) {
        onQueryChange?.("");
        return onSelect(
          freeText ? customText(props.value, props.data) : props.data,
          status
        );
      }
    },
  });

  const { handleInclusion, handleExclusion } = useIncludeExclude(
    setMenuOpened,
    onSelect,
    props
  );

  return (
    <div
      style={getStyles("option", props)}
      ref={innerRef}
      {...innerProps}
      onClick={handleInclusion}
    >
      <SafeHTML as={Highlight} html={boldifyString(children, query)} />
      {props.isFocused && (
        <Actions>
          {allowExclusion && (
            <>
              <Chip
                label={tr(locale, "UI-KIT::FORM::INCLUDE")}
                onClick={(e) => handleInclusion(e)}
                status={status === "include" ? "include" : "none"}
                size="small"
              />
              <Chip
                label={tr(locale, "UI-KIT::FORM::EXCLUDE")}
                onClick={(e) => handleExclusion(e)}
                status={status === "exclude" ? "exclude" : "none"}
                size="small"
              />
            </>
          )}
        </Actions>
      )}
    </div>
  );
};

const SelectedOptions = styled.div<{ freeText: boolean }>`
  flex: 1 0 32px;
  margin-top: ${({ freeText }) => (freeText ? "71px" : "24px")};
  margin-bottom: ${Spacing.Medium};
  display: flex;
  flex-wrap: wrap;
  column-gap: ${Spacing.ExtraSmall};
  row-gap: ${Spacing.ExtraSmall};
  align-items: flex-start;
`;

const StyledCreatableSelect = styled(CreatableSelect)`
  position: absolute;
  top: 0;
  width: 100%;
`;

const useInputFocus = (onClickOutside, autoFocus) => {
  const inputRef = useRef<any>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(selectRef, onClickOutside);

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return { inputRef, selectRef };
};

const UiKitFormAutocomplete = React.memo(
  ({
    choices,
    onSelect,
    onQueryChange,
    placeholder = "search",
    allowExclusion = false,
    freeText = false,
    locale,
    noOptionMessage = "No Options",
    firstMount,
    autoFocus,
    onClickOutside,
    tooltip,
    onClickSelectedOption,
    query,
  }: IProps) => {
    const [menuOpened, setMenuOpened] = useState(false);
    const theme = useTheme();
    const [status, setStatus] = useState("include");
    const { refinedSelectedChoices } = checkFilledCategories(choices);

    useKeyboardShortcuts("autocomplete", {
      left: () => setStatus("include"),
      right: () => setStatus(allowExclusion ? "exclude" : "none"),
    });

    const availableChoices = useMemo(
      () =>
        sortBy(
          choices.filter((choice) => !refinedSelectedChoices.includes(choice)),
          "label"
        ),
      [choices, refinedSelectedChoices]
    );

    const onChange = (element, action) => {
      if (get(action, "action", "") === "select-option") {
        onSelect(element, "include");
      }
    };

    const customComponents = {
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      Option: ({ children, getStyles, innerRef, innerProps, ...props }) => (
        <CustomOption
          {...{
            children,
            getStyles,
            onSelect,
            onQueryChange,
            refinedSelectedChoices,
            locale,
            status,
            allowExclusion,
            freeText,
            innerRef,
            innerProps,
            setMenuOpened,
            query,
            ...props,
          }}
        />
      ),
    };

    const { selectRef, inputRef } = useInputFocus(onClickOutside, autoFocus);

    return (
      <>
        {freeText ? (
          <div style={{ position: "relative", height: "1px" }} ref={selectRef}>
            <StyledCreatableSelect
              value={[]}
              isClearable={true}
              options={availableChoices}
              styles={getCustomStyles(theme, firstMount)}
              components={customComponents}
              onInputChange={onQueryChange}
              placeholder={placeholder}
              noOptionsMessage={() =>
                choices.length > 0 ? noOptionMessage : <Loader size="s" />
              }
              onMenuOpen={() => setMenuOpened(true)}
              onMenuClose={() => setMenuOpened(false)}
              menuIsOpen={menuOpened}
              onChange={onChange}
              ref={inputRef}
            />
          </div>
        ) : (
          <Select
            value={availableChoices.find((choice) => choice.id === query)}
            isClearable={true}
            options={
              availableChoices as OptionsOrGroups<Choice, GroupBase<Choice>>
            }
            styles={getCustomStyles(theme, firstMount)}
            components={customComponents}
            onInputChange={onQueryChange}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionMessage}
            onMenuOpen={() => setMenuOpened(true)}
            onMenuClose={() => setMenuOpened(false)}
            menuIsOpen={menuOpened}
            ref={inputRef}
          />
        )}
        <SelectedOptions freeText={freeText}>
          {map(refinedSelectedChoices, (choice) => {
            const chip = (
              <Chip
                key={choice.id}
                onClick={() =>
                  onClickSelectedOption && onClickSelectedOption(choice)
                }
                label={choice.label}
                status={choice.status}
                onClickClose={() => onSelect(choice, "none")}
                size="small"
              />
            );
            return tooltip ? (
              <Tooltip key={choice.id} tooltip={tooltip(choice.id as string)}>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </SelectedOptions>
      </>
    );
  }
);

export default UiKitFormAutocomplete;
